import { useState } from 'react';
import { api, type DriftPolicy, type ManifestPreview } from '../../api';
import { formatDateTime } from '../../format';
import { useAction } from '../../hooks';
import { formatValue } from '../FlagsTab';
import { Alert } from '../Alert';
import { Field } from '../Field';
import { DRIFT_LABELS } from '../labels';
import { Tag } from '../Tag';

function DriftForm({
  instanceId,
  current,
  onSaved,
}: {
  instanceId: string;
  current: DriftPolicy;
  onSaved: () => void;
}) {
  const action = useAction();
  const [value, setValue] = useState<DriftPolicy>(current);
  const [saved, setSaved] = useState(false);

  async function save() {
    setSaved(false);
    if ((await action.run(() => api.setDriftPolicy(instanceId, value))) !== undefined) {
      setSaved(true);
      onSaved();
    }
  }

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <Field
        label="Politique de dérive"
        hint="enforce : l'agent corrige en continu un plugin installé ou modifié à la main. report-only : il le signale seulement."
      >
        {(p) => (
          <select {...p} value={value} onChange={(e) => setValue(e.target.value as DriftPolicy)}>
            {(Object.keys(DRIFT_LABELS) as DriftPolicy[]).map((d) => (
              <option key={d} value={d}>
                {DRIFT_LABELS[d]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <button type="submit" className="btn" disabled={action.busy || value === current}>
        Enregistrer la politique
      </button>
      {saved && value === current && <span className="muted">Politique enregistrée.</span>}
      {action.error && <Alert>{action.error}</Alert>}
    </form>
  );
}

/** Politique de dérive et aperçu du manifeste calculé (non signé : l'agent reçoit la version signée, sans avertissements). */
export function ManifestPanel({
  instanceId,
  preview,
  error,
  onChanged,
}: {
  instanceId: string;
  preview: ManifestPreview | null;
  error: string | null;
  onChanged: () => void;
}) {
  const m = preview?.manifest;
  return (
    <div className="stack">
      {error && <Alert>{error}</Alert>}
      {!preview && !error && (
        <p className="muted" aria-busy="true">
          Calcul du manifeste…
        </p>
      )}
      {m && (
        <>
          <DriftForm
            key={m.driftPolicy}
            instanceId={instanceId}
            current={m.driftPolicy}
            onSaved={onChanged}
          />

          <div className="row-actions">
            <Tag tone="gray">Aperçu non signé</Tag>
            <span>
              Révision <strong>{m.revision}</strong>
            </span>
            <span className="muted">calculée le {formatDateTime(m.generatedAt)}</span>
            <span className="muted">canal {m.channel}</span>
          </div>

          {preview.warnings.length > 0 && (
            <Alert kind="warning">
              <strong>
                {preview.warnings.length} avertissement{preview.warnings.length > 1 ? 's' : ''}
              </strong>{' '}
              (non transmis à l'agent) :
              <ul className="plain-list">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}

          <h3>Plugins du manifeste ({m.plugins.length})</h3>
          {m.plugins.length === 0 ? (
            <p className="muted">
              Aucun plugin : seuls les paquets déjà présents dans l'image sont conservés.
            </p>
          ) : (
            <div className="table-wrap">
              <table>
                <caption className="sr-only">Plugins du manifeste</caption>
                <thead>
                  <tr>
                    <th scope="col">Paquet</th>
                    <th scope="col">Version</th>
                    <th scope="col">Source</th>
                    <th scope="col">Activé</th>
                  </tr>
                </thead>
                <tbody>
                  {m.plugins.map((p) => (
                    <tr key={p.package}>
                      <th scope="row">
                        <code>{p.package}</code>
                      </th>
                      <td>{p.version}</td>
                      <td>
                        {p.source === 'tarball' ? 'Archive' : 'Registre npm'}
                        {p.url && <div className="url muted">{p.url}</div>}
                      </td>
                      <td>{p.enabled ? 'Oui' : 'Non'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3>Packs de skills du manifeste ({m.skills.length})</h3>
          {m.skills.length === 0 ? (
            <p className="muted">Aucun pack de skills.</p>
          ) : (
            <div className="table-wrap">
              <table>
                <caption className="sr-only">Packs de skills du manifeste</caption>
                <thead>
                  <tr>
                    <th scope="col">Pack</th>
                    <th scope="col">Espace de noms</th>
                    <th scope="col">Dépôt</th>
                    <th scope="col">Ref · chemin</th>
                  </tr>
                </thead>
                <tbody>
                  {m.skills.map((s) => (
                    <tr key={s.namespace}>
                      <th scope="row">{s.name}</th>
                      <td>
                        <code>{s.namespace}</code>
                      </td>
                      <td className="url">{s.repo}</td>
                      <td>
                        <code>{s.ref}</code>
                        {s.path && (
                          <>
                            {' · '}
                            <code>{s.path}</code>
                          </>
                        )}
                        {s.locked && (
                          <>
                            {' '}
                            <Tag tone="gray">Verrouillé</Tag>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3>Flags résolus ({Object.keys(m.flags).length})</h3>
          {Object.keys(m.flags).length === 0 ? (
            <p className="muted">Aucun flag défini.</p>
          ) : (
            <ul className="plain-list">
              {Object.entries(m.flags)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([k, v]) => (
                  <li key={k}>
                    <code>{k}</code> = {formatValue(v)}
                  </li>
                ))}
            </ul>
          )}

          <details className="disclosure">
            <summary>Manifeste brut (JSON, non signé)</summary>
            <pre className="json">{JSON.stringify(m, null, 2)}</pre>
          </details>
        </>
      )}
    </div>
  );
}
